import { Static, Type } from '@sinclair/typebox';
import { Subcription } from './namespace';
import { BillingPeriod } from './subscription';

// =============================================================================
// Enums (mirror of Prisma enums in packages/backend/prisma-pg/schema.prisma)
// =============================================================================

//⚠️IMPACT attention changer dans le fichier prisma l'enum PromoCodeKind
export enum PromoCodeKind {
  PERCENT = 'PERCENT',
  FIXED_AMOUNT_MINOR = 'FIXED_AMOUNT_MINOR',
  LOCKED_PRICE_MINOR = 'LOCKED_PRICE_MINOR',
  // Enveloppe budgétaire : `value` = budget total (centimes). Remise consommée
  // facture après facture jusqu'à épuisement (burn-down), appliquée APRÈS les autres
  // promos (donc cumulable, y compris avec une remise à vie non-cumulable). C'est une
  // remise sur le HT (pas un avoir), gérée dans le segment-scheduler, pas dans applyPromos.
  BUDGET_MINOR = 'BUDGET_MINOR',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum VoidOnPlanChange
export enum VoidOnPlanChange {
  NEVER = 'NEVER',
  DOWNGRADE_ONLY = 'DOWNGRADE_ONLY',
  ANY_CHANGE = 'ANY_CHANGE',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum PromoVoidReason
export enum PromoVoidReason {
  PLAN_CHANGE = 'PLAN_CHANGE',
  DOWNGRADE = 'DOWNGRADE',
  ADDON_ADDED = 'ADDON_ADDED',
  EXPIRED = 'EXPIRED',
  MAX_REDEMPTIONS_REACHED = 'MAX_REDEMPTIONS_REACHED',
  MANUAL_ADMIN = 'MANUAL_ADMIN',
}

// Raison pour laquelle un code n'est pas appliqué sur un segment donné (filtrage non destructif).
// À ne pas confondre avec PromoVoidReason qui marque une mort définitive de la redemption.
export enum PromoIneligibilityReason {
  NOT_ELIGIBLE_PLAN = 'NOT_ELIGIBLE_PLAN',
  NOT_ELIGIBLE_SIZE = 'NOT_ELIGIBLE_SIZE',
  NOT_ELIGIBLE_BILLING = 'NOT_ELIGIBLE_BILLING',
  EXPIRED = 'EXPIRED',
  MAX_REDEMPTIONS_REACHED = 'MAX_REDEMPTIONS_REACHED',
  ALREADY_REDEEMED = 'ALREADY_REDEEMED',
  STACKING_FORBIDDEN = 'STACKING_FORBIDDEN',
  CODE_NOT_FOUND = 'CODE_NOT_FOUND',
  CODE_INACTIVE = 'CODE_INACTIVE',
  OUTSIDE_VALIDITY_WINDOW = 'OUTSIDE_VALIDITY_WINDOW',
  TARGETED_TO_OTHER_PRO = 'TARGETED_TO_OTHER_PRO',
  // Spécifique au checkout : la remise annulerait le montant dû. Un paiement à 0 € bascule
  // côté Stripe sur un SetupIntent (aucun débit), que la page de paiement ne sait pas confirmer.
  ZERO_AMOUNT_AT_CHECKOUT = 'ZERO_AMOUNT_AT_CHECKOUT',
  // Spécifique au checkout : le code est valide mais ne remise aucune ligne du panier (ex. code
  // réservé à une option que le pro n'a pas choisie). L'accepter afficherait « −0 € ».
  NOT_APPLICABLE_TO_ORDER = 'NOT_APPLICABLE_TO_ORDER',
}

// String unions partagés (cohérents avec subscription.ts → PlanSize, QuantityAddonCode)
const SizeLiteral = Type.Union([Type.Literal('solo'), Type.Literal('team')]);
const QuantityAddonLiteral = Type.Union([
  Type.Literal('extra_user'),
  Type.Literal('extra_gmb'),
  Type.Literal('extra_domain'),
  Type.Literal('seo_extra_backlink'),
  Type.Literal('seo_articles_double'),
]);

// =============================================================================
// Admin CRUD
// =============================================================================

export const PromoCodeCreateRequestSchema = Type.Object({
  code: Type.String({ minLength: 1, maxLength: 64, pattern: '^[A-Z0-9_-]+$' }),
  label: Type.String({ minLength: 1, maxLength: 255 }),
  description: Type.Optional(Type.String({ maxLength: 2000 })),

  kind: Type.Enum(PromoCodeKind),
  value: Type.Integer({ minimum: 0 }),
  // Plancher optionnel (HT, centimes) : la facture ne descend pas sous ce montant
  // quand le code s'applique. null/absent = pas de plancher.
  min_total_minor: Type.Optional(Type.Integer({ minimum: 0 })),
  duration_months: Type.Optional(Type.Integer({ minimum: 1 })),

  valid_from: Type.String({ format: 'date-time' }),
  valid_until: Type.Optional(Type.String({ format: 'date-time' })),

  max_redemptions: Type.Optional(Type.Integer({ minimum: 1 })),

  is_active: Type.Boolean(),
  is_stackable: Type.Boolean(),

  applies_to_plan: Type.Boolean(),
  applies_to_addons: Type.Boolean(),
  eligible_plans: Type.Array(Type.Enum(Subcription)),
  eligible_sizes: Type.Array(SizeLiteral),
  eligible_billing: Type.Array(Type.Enum(BillingPeriod)),
  eligible_addon_codes: Type.Array(QuantityAddonLiteral),

  void_on_plan_change: Type.Enum(VoidOnPlanChange),
  void_on_addon_added: Type.Boolean(),

  generated_for_pro_id: Type.Optional(Type.Integer({ minimum: 1 })),
  notes_admin: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type PromoCodeCreateRequestObject = Static<typeof PromoCodeCreateRequestSchema>;

export const PromoCodeUpdateRequestSchema = Type.Partial(
  Type.Object({
    label: Type.String({ minLength: 1, maxLength: 255 }),
    description: Type.String({ maxLength: 2000 }),
    valid_from: Type.String({ format: 'date-time' }),
    valid_until: Type.String({ format: 'date-time' }),
    max_redemptions: Type.Integer({ minimum: 1 }),
    is_active: Type.Boolean(),
    notes_admin: Type.String({ maxLength: 2000 }),
  }),
);
export type PromoCodeUpdateRequestObject = Static<typeof PromoCodeUpdateRequestSchema>;

// Génération d'un code 1-pro (migration V1→V2 ou deal commercial).
// Crée et auto-redeem en une opération.
export const PromoCodeGenerateForProRequestSchema = Type.Object({
  pro_id: Type.Integer({ minimum: 1 }),
  label: Type.String({ minLength: 1, maxLength: 255 }),
  kind: Type.Enum(PromoCodeKind),
  value: Type.Integer({ minimum: 0 }),
  min_total_minor: Type.Optional(Type.Integer({ minimum: 0 })),
  duration_months: Type.Optional(Type.Integer({ minimum: 1 })),
  applies_to_plan: Type.Boolean(),
  applies_to_addons: Type.Boolean(),
  eligible_plans: Type.Array(Type.Enum(Subcription)),
  eligible_sizes: Type.Array(SizeLiteral),
  eligible_billing: Type.Array(Type.Enum(BillingPeriod)),
  eligible_addon_codes: Type.Array(QuantityAddonLiteral),
  void_on_plan_change: Type.Enum(VoidOnPlanChange),
  void_on_addon_added: Type.Boolean(),
  notes_admin: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type PromoCodeGenerateForProRequestObject = Static<typeof PromoCodeGenerateForProRequestSchema>;

export const PromoCodeSchema = Type.Object({
  subscription_promo_code_id: Type.Integer(),
  code: Type.String(),
  label: Type.String(),
  description: Type.Optional(Type.String()),

  kind: Type.Enum(PromoCodeKind),
  value: Type.Integer(),
  min_total_minor: Type.Optional(Type.Integer()),
  duration_months: Type.Optional(Type.Integer()),

  valid_from: Type.String({ format: 'date-time' }),
  valid_until: Type.Optional(Type.String({ format: 'date-time' })),
  valid_from_string: Type.String({ format: 'date-time', meta: { source_key: 'valid_from', is_short: true } }),
  valid_until_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'valid_until', is_short: true } }),
  ),

  max_redemptions: Type.Optional(Type.Integer()),
  current_redemptions: Type.Integer(),

  is_active: Type.Boolean(),
  is_stackable: Type.Boolean(),

  applies_to_plan: Type.Boolean(),
  applies_to_addons: Type.Boolean(),
  eligible_plans: Type.Array(Type.Enum(Subcription)),
  eligible_sizes: Type.Array(SizeLiteral),
  eligible_billing: Type.Array(Type.Enum(BillingPeriod)),
  eligible_addon_codes: Type.Array(QuantityAddonLiteral),

  void_on_plan_change: Type.Enum(VoidOnPlanChange),
  void_on_addon_added: Type.Boolean(),

  generated_for_pro_id: Type.Optional(Type.Integer()),
  notes_admin: Type.Optional(Type.String()),
});
export type PromoCodeObject = Static<typeof PromoCodeSchema>;

export const PromoCodeListResponseSchema = Type.Object({
  items: Type.Array(PromoCodeSchema),
  total: Type.Integer(),
});
export type PromoCodeListResponseObject = Static<typeof PromoCodeListResponseSchema>;

// Réponse générique d'action admin (toggle, révocation, etc.)
export const PromoCodeActionResponseSchema = Type.Object({
  success: Type.Boolean(),
});
export type PromoCodeActionResponseObject = Static<typeof PromoCodeActionResponseSchema>;

// Vue admin d'une redemption (paire pro ↔ code) — pour lister les bénéficiaires
// d'un code et révoquer manuellement (void_reason = MANUAL_ADMIN).
export const AdminPromoRedemptionSchema = Type.Object({
  redemption_id: Type.Integer(),
  professional_id: Type.Integer(),
  professional_name: Type.Optional(Type.String()),
  is_active: Type.Boolean(), // non voided & non expirée
  started_at_string: Type.String({ format: 'date-time', meta: { source_key: 'started_at', is_short: true } }),
  ends_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'ends_at', is_short: true } })),
  voided_at_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'voided_at', is_short: true } }),
  ),
  void_reason: Type.Optional(Type.Enum(PromoVoidReason)),
});
export type AdminPromoRedemptionObject = Static<typeof AdminPromoRedemptionSchema>;

export const AdminPromoRedemptionListResponseSchema = Type.Object({
  items: Type.Array(AdminPromoRedemptionSchema),
  total: Type.Integer(),
});
export type AdminPromoRedemptionListResponseObject = Static<typeof AdminPromoRedemptionListResponseSchema>;

// Révocation manuelle d'une redemption par un admin (note optionnelle pour l'audit).
export const AdminPromoRedemptionRevokeRequestSchema = Type.Object({
  reason_note: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type AdminPromoRedemptionRevokeRequestObject = Static<typeof AdminPromoRedemptionRevokeRequestSchema>;

// =============================================================================
// Pro flow — saisie d'un code, preview, confirm, listing actif
// =============================================================================

export const PromoCodeRedeemPreviewRequestSchema = Type.Object({
  code: Type.String({ minLength: 1, maxLength: 64 }),
});
export type PromoCodeRedeemPreviewRequestObject = Static<typeof PromoCodeRedeemPreviewRequestSchema>;

export const PromoCodeRedeemPreviewResponseSchema = Type.Object({
  is_eligible: Type.Boolean(),
  // Si is_eligible=false → raison
  ineligibility_reason: Type.Optional(Type.Enum(PromoIneligibilityReason)),

  // Si is_eligible=true → infos d'application
  promo_label: Type.Optional(Type.String()),
  ends_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'ends_at', is_short: true } })),

  // Preview du prochain segment avec/sans le code
  current_segment_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'current_segment_amount_minor' } }),
  ),
  next_segment_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'next_segment_amount_minor' } }),
  ),
  monthly_savings: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'monthly_savings_minor' } })),
});
export type PromoCodeRedeemPreviewResponseObject = Static<typeof PromoCodeRedeemPreviewResponseSchema>;

export const PromoCodeRedeemConfirmRequestSchema = Type.Object({
  code: Type.String({ minLength: 1, maxLength: 64 }),
});
export type PromoCodeRedeemConfirmRequestObject = Static<typeof PromoCodeRedeemConfirmRequestSchema>;

export const PromoCodeRedeemConfirmResponseSchema = Type.Object({
  redemption_id: Type.Integer(),
  promo_label: Type.String(),
  ends_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'ends_at', is_short: true } })),
});
export type PromoCodeRedeemConfirmResponseObject = Static<typeof PromoCodeRedeemConfirmResponseSchema>;

// =============================================================================
// Checkout flow — saisie d'un code sur la page de paiement (1re souscription)
// =============================================================================
//
// Le flow « pro » ci-dessus exige un `pro_subscription` existant : il ne couvre donc
// PAS la première souscription, où la ligne d'abonnement n'est créée qu'au webhook de
// paiement. Ces schémas alimentent les endpoints publics `/public/payment/promo/*`,
// scopés par l'uuid porteur du paiement (même modèle de sécurité que billing-info).

export const CheckoutPromoApplyRequestSchema = Type.Object({
  uuid: Type.String({ minLength: 1, maxLength: 128 }),
  code: Type.String({ minLength: 1, maxLength: 64 }),
});
export type CheckoutPromoApplyRequestObject = Static<typeof CheckoutPromoApplyRequestSchema>;

export const CheckoutPromoRemoveRequestSchema = Type.Object({
  uuid: Type.String({ minLength: 1, maxLength: 128 }),
});
export type CheckoutPromoRemoveRequestObject = Static<typeof CheckoutPromoRemoveRequestSchema>;

// Réponse commune preview / apply / remove : l'état du code sur ce checkout.
export const CheckoutPromoStateSchema = Type.Object({
  is_eligible: Type.Boolean(),
  // Si is_eligible=false → raison (traduite côté front via TRANSLATE_KEY_PROMO_INELIGIBILITY)
  ineligibility_reason: Type.Optional(Type.Enum(PromoIneligibilityReason)),

  // Si is_eligible=true → impact sur le montant réglé aujourd'hui
  code: Type.Optional(Type.String()),
  promo_label: Type.Optional(Type.String()),
  discount_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'discount_amount_minor' } })),
  new_amount_all_taxes: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'new_amount_all_taxes_minor' } }),
  ),
  // Portée de la remise sur les échéances suivantes (la redemption est créée à
  // l'encaissement : elle continue de réduire les segments facturés ensuite).
  is_forever: Type.Optional(Type.Boolean()),
  ends_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'ends_at', is_short: true } })),
});
export type CheckoutPromoStateObject = Static<typeof CheckoutPromoStateSchema>;

// Listing des redemptions actives pour le pro (affichage UI subscription page)
export const ActivePromoRedemptionSchema = Type.Object({
  redemption_id: Type.Integer(),
  code: Type.String(),
  label: Type.String(),
  description: Type.Optional(Type.String()),
  kind: Type.Enum(PromoCodeKind),
  value: Type.Integer(),
  is_stackable: Type.Boolean(),
  started_at_string: Type.String({ format: 'date-time', meta: { source_key: 'started_at', is_short: true } }),
  ends_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'ends_at', is_short: true } })),
  is_forever: Type.Boolean(),
});
export type ActivePromoRedemptionObject = Static<typeof ActivePromoRedemptionSchema>;

export const ActivePromoRedemptionsResponseSchema = Type.Object({
  items: Type.Array(ActivePromoRedemptionSchema),
});
export type ActivePromoRedemptionsResponseObject = Static<typeof ActivePromoRedemptionsResponseSchema>;
